'use client';
import { getGlobalFilterQuery } from '@/lib/global-filter/get-global-filter-query';
import { ObjectKeys } from '@/lib/utils';
import { fetchApi } from '@/requests/api';
import { BASE_BACKEND_URL } from '@/requests/constants';
import type { Meta } from '@/requests/types';
import type { SignableDocument } from '@pigello/pigello-matrix';
import { useQuery } from '@tanstack/react-query';
import { camelCase } from 'es-toolkit/compat';

export default function useGetPendingSignings({
  countOnly,
}: {
  countOnly?: boolean;
} = {}) {
  const fetchPendingSignings = async () => {
    const query = await getGlobalFilterQuery(true);

    const baseUrl = `${BASE_BACKEND_URL}/documents/signing/signabledocument/tosign/`;

    const queryParams = {
      _page: '1',
      _page_size: countOnly ? '1' : '500',
      signing_aborted_time__isnull: 'true',
      rejected_time__isnull: 'true',
      attached_to__isnull: 'true',
      signed_time__isnull: 'true',
      _order_by: '-signing_started_time',
    };

    const searchParams = new URLSearchParams(queryParams);

    // Append the global filter query if it exists
    const fullUrl = `${baseUrl}?${searchParams.toString()}${query}`;

    const res = await fetchApi({
      url: fullUrl,
      method: 'GET',
    });

    const { data, meta } = await res.json();

    const cameled = data.map((dp: object) => {
      return ObjectKeys(dp).reduce<Record<string, unknown>>((acc, cur) => {
        acc[camelCase(cur as string)] = dp[cur];

        return acc;
      }, {});
    });

    return { data: cameled as SignableDocument[], meta };
  };

  const { data: pending, isLoading: loadingPending } = useQuery<{
    data: SignableDocument[];
    meta: Meta;
  }>({
    queryKey: [`pending-signings_${countOnly ? 'count' : 'list'}`],
    queryFn: fetchPendingSignings,
    staleTime: countOnly ? 60 * 5 * 1000 : undefined,
  });

  return {
    count: pending?.meta.total_amount ?? 0,
    loading: loadingPending,
    data: pending,
  };
}
